import { ActivityCard } from "@adsk/alloy-react-activity-log";
import InfiniteScroll from "@adsk/bim360-matrix-react-infinite-scroll";
import React, { useEffect, useMemo, useState } from "react";
import { ActivityBlock, ActivityChange } from "@adsk/acc-issues-types";
import { EmptyStateIssuesFetchError } from "@adsk/acc-issues-common/components/display/EmptyStates";
import {
  getNextOffsetByLinkType,
  PageLink,
} from "../../IssuesTab/IssueLog/IssueTable/pagination";
import {
  getActivityLog,
  getActivityLogPagination,
  getHasActivityLogFetchErrored,
  getHasNextActivityLogPage,
} from "@adsk/acc-issues-core/state/redux/ducks/issueActivityLog";
import { getCustomFieldEntitiesMap } from "@adsk/acc-issues-core/state/redux/ducks/issueCustomFields";
import { getIssueRootCausesEntitiesMap } from "@adsk/acc-issues-core/state/redux/ducks/issueRootCauses";
import { getIssueTypeEntitiesMap } from "@adsk/acc-issues-core/state/redux/ducks/issueTypes";
import { useSelector } from "@adsk/acc-issues-core/state/redux/hooks";
import { AbsoluteCenteredSpinner } from "@adsk/acc-issues-common/components/primitive/CenteredSpinner";
import { getDateStringAsDisplayText } from "@adsk/acc-issues-common/utils/date/dateUtils";
import {
  FeatureFlag,
  useFeatureFlag,
} from "@adsk/acc-issues-core/utils/featureFlags";
import { useTranslations } from "@adsk/acc-issues-translations";
import { IssueCommentBlock } from "../IssueCommentBlock";
import { AggregatedChangesActivityCard } from "./ActivityCards/AggregatedChangesActivityCard";
import { ChangedAssigneeActivityCard } from "./ActivityCards/ChangedAssigneeActivityCard";
import { ChangedReferenceActivityCard } from "./ActivityCards/ChangedReferenceActivityCard";
import { ChangedStatusActivityCard } from "./ActivityCards/ChangedStatusActivityCard";
import { ChangedTextActivityCard } from "./ActivityCards/ChangedTextActivityCard";
import { ChangedValueActivityCard } from "./ActivityCards/ChangedValueActivityCard";
import { ChangedWatcherActivityCard } from "./ActivityCards/ChangedWatcherActivityCard";
import { activityChangeType, activityVerbs } from "@adsk/acc-issues-types";
import { parseActivitiesData } from "./activityLogParse";
import {
  ActivityLogCardCommonProps,
  ActivityLogCardProps,
} from "./activityLogTypes";
import { ChangedLinkedDocument } from "./ActivityCards/ChangedLinkedDocument";
import { PlacementActivityCard } from "./ActivityCards/PlacementActivityCard";
import { LocationNameDisplay } from "@adsk/acc-locations-components";
import { MatrixProvider } from "../../utils/components/MatrixProvider";
import { IssueDeletedActivityCard } from "./ActivityCards/IssueDeletedActivityCard";
import { ChangedSnapshotActivityCard } from "./ActivityCards/ChangedSnapshotActivityCard";
import { useFetchActivityLogAction } from "./activityLogHooks";
import { AttachmentActivityCard } from "./ActivityCards/AttachmentActivityCard";
import { IssueSuggestionGeneratedActivityCard } from "./ActivityCards/IssueSuggestionGeneratedActivityCard";
import { useGetProjectActorByOxygenId } from "../../hooks/useGetProjectActorByOxygenId";
import { ReferencesDataProvider } from "../../input-field-components/ReferencePickerField/ReferencesDataProvider";
import { BcfAggregatedChangesActivityCard } from "./ActivityCards/BcfAggregatedChangesActivityCard";

enum ImportTypes {
  BCF = "bcf",
  EXCEL = "excel",
}

const getActivityTitle = ({
  before,
  after,
  changeType,
  T,
  isAggregated = false,
  property = "",
}: {
  before: ActivityChange["before"];
  after: ActivityChange["after"];
  changeType: ActivityChange["changeType"];
  T: ReturnType<typeof useTranslations>["T"];
  isAggregated: boolean;
  property: React.ReactNode;
}) => {
  if (changeType === activityChangeType.IssueDeleted) {
    return T.translate("activityLog.issueWasDeleted");
  }

  if (changeType === activityChangeType.IssueRestored) {
    return T.translate("activityLog.issueWasRestored");
  }

  if (isAggregated) {
    return (
      property ||
      T.translate(`activityLog.aggregatedLabels.${changeType}`) ||
      ""
    );
  } else if (!before && after) {
    return T.translate(`activityLog.propertyAdded.${changeType}`, { property });
  } else if (before && !after) {
    return T.translate(`activityLog.propertyRemoved.${changeType}`, {
      property,
    });
  } else {
    return T.translate(`activityLog.propertyUpdated.${changeType}`, {
      property,
    });
  }
};

export const getActivityCard = ({
  activityChange,
  commonActivityBlockProps,
  T,
  rootCauseMap,
  typesMap,
  activityLogShowLocationFeatureFlag,
}: {
  activityChange: ActivityChange;
  commonActivityBlockProps: ActivityLogCardCommonProps;
  T: ReturnType<typeof useTranslations>["T"];
  rootCauseMap: ReturnType<typeof getIssueRootCausesEntitiesMap>;
  typesMap: ReturnType<typeof getIssueTypeEntitiesMap>;
  activityLogShowLocationFeatureFlag?: boolean;
}) => {
  const commonActivityProps = {
    activityChange,
    ...commonActivityBlockProps,
  } as ActivityLogCardProps;
  let { before, after } = activityChange;
  const { changeType } = activityChange;

  if (!commonActivityBlockProps.isAggregated) {
    const customTitle =
      changeType === activityChangeType.ChangedCustomAttributes
        ? activityChange.customAttributeTitle
        : undefined;

    // `before` and `after` of snapshot changes are always stringified objects, so we need to parse them to see if there is an actual value
    if (changeType === activityChangeType.ChangedSnapshot) {
      before = JSON.parse(before)?.urn;
      after = JSON.parse(after)?.urn;
    }

    Object.assign(commonActivityProps, {
      title: getActivityTitle({
        changeType,
        before,
        after,
        T,
        isAggregated: false,
        property: customTitle,
      }),
    });
  }

  switch (changeType) {
    case activityChangeType.ChangedTitle:
      return <ChangedValueActivityCard {...commonActivityProps} />;
    case activityChangeType.ChangedRootCause:
      const getRootCauseDisplayText = (rootCauseId: string): any =>
        rootCauseId &&
        (rootCauseMap[rootCauseId]
          ? rootCauseMap[rootCauseId].title
          : T.translate("activityLog.deletedRootCause"));
      return (
        <ChangedValueActivityCard
          {...commonActivityProps}
          getDisplayText={getRootCauseDisplayText}
        />
      );
    case activityChangeType.ChangedCustomAttributes: {
      if (!activityChange.before && !activityChange.after) {
        return (
          <ChangedTextActivityCard
            title={activityChange.customAttributeTitle}
            text={T.translate("activityLog.deletedCustomField")}
            {...commonActivityProps}
          />
        );
      }
      return (
        <ChangedValueActivityCard
          title={activityChange.customAttributeTitle}
          {...commonActivityProps}
        />
      );
    }
    case activityChangeType.ChangedPublished:
      return (
        <ActivityCard
          /* Used by globals.ts to amend UI issues until https://jira.autodesk.com/browse/WPLT-1164 is fixed */
          className="IssuesActivityLog__activity-card"
          title={T.translate(
            "activityLog.titles." +
              (activityChange.after === "true" ? "published" : "unpublished")
          )}
          {...commonActivityBlockProps}
        />
      );
    case activityChangeType.ChangedStatus:
      return <ChangedStatusActivityCard {...commonActivityProps} />;
    case activityChangeType.ChangedIssueType:
    case activityChangeType.ChangedIssueSubtype: {
      const key =
        "activityLog." + changeType === activityChangeType.ChangedIssueSubtype
          ? "activityLog.deletedType"
          : "activityLog.deletedCategory";
      return (
        <ChangedValueActivityCard
          {...commonActivityProps}
          getDisplayText={(issueId: string) =>
            typesMap[issueId] ? typesMap[issueId].title : T.translate(key)
          }
        />
      );
    }
    case activityChangeType.ChangedAssignedTo:
      return <ChangedAssigneeActivityCard {...commonActivityProps} />;
    case activityChangeType.ChangedStartDate:
    case activityChangeType.ChangedDueDate:
      return (
        <ChangedValueActivityCard
          {...commonActivityProps}
          getDisplayText={getDateStringAsDisplayText}
        />
      );
    case activityChangeType.ChangedDescription:
      return <ChangedTextActivityCard {...commonActivityProps} />;
    case activityChangeType.ChangedLocationDescription:
      return <ChangedValueActivityCard {...commonActivityProps} />;
    case activityChangeType.ChangedBody:
      return (
        <ChangedTextActivityCard
          {...commonActivityProps}
          title={T.translate("activityLog.titles.aCommentWasAdded")}
          text={<IssueCommentBlock commentBody={activityChange.after} />}
        />
      );
    case activityChangeType.ChangedReference:
      return <ChangedReferenceActivityCard {...commonActivityProps} />;
    case activityChangeType.ChangedWatchers:
      return <ChangedWatcherActivityCard {...commonActivityProps} />;
    // This will drop unsupported/unneeded changes.
    case activityChangeType.Placement:
      return (
        <PlacementActivityCard
          commonActivityProps={commonActivityProps}
          placement={activityChange.object.placement}
          verb={activityChange.verb}
        />
      );
    case activityChangeType.Attachment:
      return (
        <AttachmentActivityCard
          commonActivityProps={commonActivityProps}
          attachment={activityChange.object.attachment}
          verb={activityChange.verb}
        />
      );
    case activityChangeType.ChangedLinkedDocument:
      return (
        <ChangedLinkedDocument
          after={activityChange.after}
          before={activityChange.before}
          commonActivityProps={commonActivityProps}
        />
      );
    case activityChangeType.ChangedLocation:
      if (activityLogShowLocationFeatureFlag) {
        const getLocationText = (locationId: string) => (
          <LocationNameDisplay locationNodeId={locationId} />
        );
        return (
          <ChangedValueActivityCard
            {...commonActivityProps}
            getDisplayText={getLocationText}
          />
        );
      }
      return null;
    case activityChangeType.IssueDeleted:
      return (
        <IssueDeletedActivityCard
          commonActivityBlockProps={commonActivityBlockProps}
          title={T.translate("activityLog.issueWasDeleted")}
          comment={activityChange.object.deleteComment}
          {...commonActivityProps}
        />
      );
    case activityChangeType.IssueRestored:
      return <ChangedTextActivityCard {...commonActivityProps} />;
    case activityChangeType.ChangedSnapshot:
      return (
        <ChangedSnapshotActivityCard
          commonActivityProps={commonActivityProps}
        />
      );
    case activityChangeType.IssueSuggestionGenerated:
      return (
        <IssueSuggestionGeneratedActivityCard
          commonActivityProps={commonActivityProps}
          suggestion={activityChange.object?.issueSuggestion}
        />
      );
    default:
      return null;
  }
};

const getActivities = ({
  activityBlocks,
  T,
  getUser,
  rootCauseMap,
  typesMap,
  activityLogShowLocationFeatureFlag,
}: {
  activityBlocks: ActivityBlock[];
  T: ReturnType<typeof useTranslations>["T"];
  getUser: (oxygenId: string) => {
    name: string;
  } | null;
  rootCauseMap: ReturnType<typeof getIssueRootCausesEntitiesMap>;
  typesMap: ReturnType<typeof getIssueTypeEntitiesMap>;
  activityLogShowLocationFeatureFlag?: boolean;
  showDeletedUserName?: boolean;
}) =>
  activityBlocks.map((activityBlock, index) => {
    const { verb, changes } = activityBlock;
    const isAggregated =
      activityBlock.changes.length > 1 ||
      verb === activityVerbs.ISSUE_IMPORT_EDIT;
    const userName = getUser(activityBlock.actor.id)?.name;
    const commonActivityBlockProps = {
      isFirst: index === 0,
      isLast: index === activityBlocks.length - 1,
      isAggregated,
      user: { name: userName },
      date: activityBlock.date,
      key: `activity-card-${activityBlock.date}`,
    } as ActivityLogCardCommonProps;

    if (!isAggregated) {
      return getActivityCard({
        activityChange: activityBlock.changes[0],
        commonActivityBlockProps,
        T,
        rootCauseMap,
        typesMap,
        activityLogShowLocationFeatureFlag,
      });
    }
    // right now only quality-create-issue is aggregated

    const activityItems = activityBlock.changes
      .filter(
        activityChange =>
          // throw out published, since on the create event its always true
          activityChange.changeType !== activityChangeType.ChangedPublished
      )
      .map(activityChange => {
        const label =
          activityChange.changeType ===
          activityChangeType.ChangedCustomAttributes
            ? activityChange.customAttributeTitle
            : T.translate(
                `activityLog.aggregatedLabels.${activityChange.changeType}`
              );
        return {
          label,
          value: getActivityCard({
            activityChange,
            commonActivityBlockProps,
            T,
            rootCauseMap,
            typesMap,
            activityLogShowLocationFeatureFlag,
          }),
        };
      });
    const [change] = changes;
    if (
      change.object.source === ImportTypes.BCF &&
      (verb === activityVerbs.ISSUE_IMPORT_EDIT ||
        verb === activityVerbs.ISSUE_IMPORT_CREATE)
    ) {
      return (
        <BcfAggregatedChangesActivityCard
          {...commonActivityBlockProps}
          key={commonActivityBlockProps.key}
          verb={verb}
          activityItems={activityItems}
          isAutoPlacement={change.object?.isAutoPlacement}
        />
      );
    }

    // At this point it's either created or copied.
    const aggregatedTitle =
      verb === activityVerbs.ISSUE_COPY
        ? T.translate("activityLog.titles.issueWasCopied", {
            sourceIssueDisplayId: change.object?.sourceIssue?.displayId,
          })
        : T.translate("activityLog.titles.issueWasCreated");

    return (
      <AggregatedChangesActivityCard
        {...commonActivityBlockProps}
        key={commonActivityBlockProps.key}
        title={aggregatedTitle}
        activityItems={activityItems}
      />
    );
  });

export interface ActivityLogProps {
  issueId: string;
}

export const ActivityLog = ({ issueId }: ActivityLogProps) => {
  const { T } = useTranslations();
  const rootCauseMap = useSelector(getIssueRootCausesEntitiesMap);
  const customFieldEntitiesMap = useSelector(getCustomFieldEntitiesMap);
  const typesMap = useSelector(getIssueTypeEntitiesMap);
  const hasNextPage = useSelector(getHasNextActivityLogPage);
  const isErrored = useSelector(getHasActivityLogFetchErrored);
  const pagination = useSelector(getActivityLogPagination);
  const fetchActivityLogAction = useFetchActivityLogAction();
  const activitiesData: ActivityBlock[] = useSelector(getActivityLog);
  const getUser = useGetProjectActorByOxygenId();
  const { value: activityLogShowLocationFeatureFlag } = useFeatureFlag(
    FeatureFlag.ActivityLogShowLocation
  );
  const changeTypesToOmit = activityLogShowLocationFeatureFlag
    ? []
    : [activityChangeType.ChangedLocation];
  const activityBlocks = useMemo(
    () =>
      activitiesData &&
      parseActivitiesData(
        activitiesData,
        customFieldEntitiesMap,
        changeTypesToOmit
      ),
    [activitiesData, customFieldEntitiesMap]
  );
  const [currentlyLoadedIssueId, setIssueId] = useState<typeof issueId>();
  const activities = useMemo(
    () =>
      activityBlocks &&
      getActivities({
        activityBlocks,
        T,
        getUser,
        rootCauseMap,
        typesMap,
        activityLogShowLocationFeatureFlag,
      }),
    [
      activityBlocks,
      T,
      getUser,
      rootCauseMap,
      typesMap,
      activityLogShowLocationFeatureFlag,
    ]
  );

  const doesReferenceActivityExist = useMemo(
    () =>
      activityBlocks.some(activityBlock =>
        activityBlock.changes.some(
          change => change.changeType === activityChangeType.ChangedReference
        )
      ),
    [activityBlocks]
  );

  useEffect(() => {
    async function handler() {
      await fetchActivityLogAction(issueId);
      setIssueId(issueId);
    }

    // noinspection JSIgnoredPromiseFromCall
    handler();
  }, [issueId, fetchActivityLogAction]);

  const fetchNextActivityLogPage = async () => {
    const { offset, limit, totalResults } = pagination;
    if (hasNextPage) {
      const nextOffset = getNextOffsetByLinkType({
        limit,
        offset,
        total: totalResults,
        link: PageLink.Next,
      });
      const activitiesResponse = await fetchActivityLogAction(issueId, {
        limit,
        offset: nextOffset,
      });
      if (!activitiesResponse) {
        throw new Error("Unable to fetch next page");
      }
    }
  };

  /* handle the case of erroring on the first API call, which won't be handled by InfiniteScroll */
  if (isErrored) {
    return <EmptyStateIssuesFetchError />;
  }

  /* handle the case of loading first time and switching between issues (to not show stale data) */
  if (issueId !== currentlyLoadedIssueId) {
    return <AbsoluteCenteredSpinner />;
  }

  const renderActivities = () => (
    <div data-testid="activity-log-tab">
      <MatrixProvider>
        <InfiniteScroll
          className="IssuesActivityLog"
          style={{ paddingBottom: 30, paddingRight: 20 }}
          items={activities}
          showScrollToButton={false}
          loadMore={fetchNextActivityLogPage}
          hasMore={hasNextPage}
          tryAgainButtonText={T.translate("tryAgain")}
          errorLoadingMoreItemsText={T.translate(
            "activityLog.loadingActivityFailed"
          )}
        />
      </MatrixProvider>
    </div>
  );

  ReferencesDataProvider;

  return doesReferenceActivityExist ? (
    <ReferencesDataProvider>{renderActivities()}</ReferencesDataProvider>
  ) : (
    renderActivities()
  );
};
